import { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkPlus, Check, MoreHorizontal, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, apiError } from "@/lib/api";

const slug = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const clean = (filters) => {
  const out = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    const text = String(value ?? "").trim();
    if (text && text !== "all") out[key] = text;
  });
  return out;
};

const same = (a, b) => {
  const one = clean(a);
  const two = clean(b);
  const keys = Object.keys(one);
  return keys.length === Object.keys(two).length && keys.every((k) => one[k] === two[k]);
};

const words = (filters) =>
  Object.entries(clean(filters))
    .map(([key, value]) => (key === "needs_fix" ? "needs a fix" : value))
    .join(" · ") || "everything";

/** An officer's own slicer combinations — kept by name, reopened in one click. */
export const SavedViews = ({ screen, filters, onApply, suggestions = [] }) => {
  const [views, setViews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const landed = useRef(false);

  const load = (openDefault = false) =>
    api.get("/views", { params: { screen } }).then((r) => {
      setViews(r.data.views || []);
      if (openDefault && !landed.current) {
        landed.current = true;
        const home = (r.data.views || []).find((v) => v.is_default);
        if (home && !Object.keys(clean(filters)).length) onApply(home.filters);
      }
    });

  useEffect(() => {
    load(true).catch(() => setViews([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const current = views.find((v) => same(v.filters, filters));
  const nothingPicked = !Object.keys(clean(filters)).length;

  const save = () =>
    api
      .post("/views", { screen, name, filters: clean(filters) })
      .then(() => {
        toast.success(`Kept as “${name.trim()}”. Tap it any time to come back to this view.`);
        setSaving(false);
        setName("");
        return load();
      })
      .catch((e) => toast.error(apiError(e)));

  const patch = (view, body, note) =>
    api
      .patch(`/views/${view.id}`, body)
      .then(() => {
        toast.success(note);
        setRenaming(null);
        return load();
      })
      .catch((e) => toast.error(apiError(e)));

  const remove = (view) =>
    api
      .delete(`/views/${view.id}`)
      .then(() => {
        toast.success(`“${view.name}” removed from your views.`);
        return load();
      })
      .catch((e) => toast.error(apiError(e)));

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="saved-views">
      <span className="label-caps flex items-center gap-1.5 text-muted-foreground">
        <Bookmark className="h-3.5 w-3.5" /> My views
      </span>

      {views.map((view) => {
        const on = current?.id === view.id;
        return (
          <div
            key={view.id}
            className={`flex items-center border transition-colors duration-200 ${
              on ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted"
            }`}
          >
            <button
              type="button"
              data-testid={`view-chip-${slug(view.name)}`}
              title={words(view.filters)}
              onClick={() => onApply(view.filters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${
                on ? "text-primary" : ""
              }`}
            >
              {view.is_default && <Star className="h-3 w-3 fill-current" />}
              {view.name}
              {on && <Check className="h-3 w-3" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={`view-menu-${slug(view.name)}`}
                  className="border-l border-border px-1.5 py-2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-none">
                <DropdownMenuItem
                  data-testid={`view-update-${slug(view.name)}`}
                  onClick={() =>
                    patch(view, { filters: clean(filters) },
                      `“${view.name}” now opens on what you are seeing.`)
                  }
                >
                  Update to what I am seeing
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`view-rename-${slug(view.name)}`}
                  onClick={() => setRenaming({ ...view })}
                >
                  Rename…
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`view-default-${slug(view.name)}`}
                  onClick={() =>
                    patch(view, { is_default: !view.is_default },
                      view.is_default
                        ? `This screen no longer opens on “${view.name}”.`
                        : `This screen will open on “${view.name}”.`)
                  }
                >
                  {view.is_default ? "Stop opening on this view" : "Open this screen on it"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`view-delete-${slug(view.name)}`}
                  onClick={() => remove(view)}
                  className="text-[hsl(var(--danger))]"
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}

      {!views.length &&
        suggestions.map((s) => (
          <button
            key={s.name}
            type="button"
            data-testid={`suggested-view-${slug(s.name)}`}
            onClick={() => onApply(s.filters)}
            className="border border-dashed border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-primary hover:text-primary"
          >
            {s.name}
          </button>
        ))}

      <Button
        type="button"
        variant="ghost"
        data-testid="save-view-button"
        disabled={nothingPicked || !!current}
        onClick={() => {
          setName("");
          setSaving(true);
        }}
        className="h-8 rounded-none px-2 font-display text-[10px] font-bold uppercase tracking-widest"
      >
        <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" />
        {current ? "This view is kept" : "Save this view"}
      </Button>
      {nothingPicked && !views.length && (
        <span className="text-[11px] text-muted-foreground">
          Set the slicers you want, then keep them here by name.
        </span>
      )}

      <Dialog open={saving} onOpenChange={setSaving}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Keep this view</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{words(filters)}</p>
          <Input
            data-testid="save-view-name"
            autoFocus
            placeholder="My unit, ongoing"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && save()}
            className="h-11 rounded-none bg-card"
          />
          <DialogFooter>
            <Button
              data-testid="save-view-confirm"
              disabled={!name.trim()}
              onClick={save}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              Keep it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Rename this view</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="rename-view-name"
            autoFocus
            value={renaming?.name || ""}
            onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
            className="h-11 rounded-none bg-card"
          />
          <DialogFooter>
            <Button
              data-testid="rename-view-confirm"
              disabled={!renaming?.name?.trim()}
              onClick={() =>
                patch(renaming, { name: renaming.name }, "The view has been renamed.")
              }
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              Save the name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
